/**
 * OnboardingRouteSync
 *
 * A renderless component that must be mounted INSIDE the React Router tree
 * (e.g. inside Header, which lives in every authenticated route).
 *
 * It watches the current pathname via useLocation() and writes it into the
 * Zustand store so that OnboardingProvider — which lives above the router and
 * cannot call useLocation() — can react to route changes.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { matchesScope, CURRENT_GUIDE, hasSeenCurrentGuide } from '../config';
import type { RouteScope } from '../config/types';
import { useOnboardingStore } from '../store/useOnboardingStore';

const SCOPES: RouteScope[] = ['home', 'project-detail', 'drawing'];

const getScopeForPath = (path: string): RouteScope | null =>
  SCOPES.find((s) => matchesScope(path, s)) ?? null;

export const OnboardingRouteSync = () => {
  const { pathname } = useLocation();
  const setPathname = useOnboardingStore((s) => s.setPathname);
  const goTo = useOnboardingStore((s) => s.goTo);
  const open = useOnboardingStore((s) => s.open);
  const isOpen = useOnboardingStore((s) => s.isOpen);
  const prevScopeRef = useRef<RouteScope | null>(null);

  useEffect(() => {
    setPathname(pathname);

    const scope = getScopeForPath(pathname);
    if (scope !== prevScopeRef.current) {
      const prevScope = prevScopeRef.current;
      prevScopeRef.current = scope;
      if (scope) {
        // Jump to the first step that belongs to this scope.
        const firstIdx = CURRENT_GUIDE.findIndex((s) => s.routeScope === scope);
        if (firstIdx !== -1) {
          goTo(firstIdx);
          // Auto-resume: if the guide is in-progress (paused, not skipped/completed)
          // and the user just navigated to a new page scope, pop it open automatically.
          // prevScope !== null guards against firing on the initial mount —
          // the initial auto-trigger is handled by useOnboarding(true).
          if (prevScope !== null && !isOpen && !hasSeenCurrentGuide()) {
            const t = setTimeout(() => open(firstIdx), 600);
            return () => clearTimeout(t);
          }
        }
      }
    }
  }, [pathname, setPathname, goTo, open, isOpen]);

  return null;
};
